import { t } from "i18next";
import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import colors from "../../../theme/colors";
import PreviewImageModal from "../../dashboard/previewImageModal/PreviewImageModal";
import AttachmentIcon from "../../shared/Icons/AttachmentIcon";
import DeleteIcon from "../../shared/Icons/DeleteIcon";
import UploadIcon from "../../shared/Icons/UploadIcon";
import ViewIcon from "../../shared/Icons/ViewIcon";
const FILE_ERRORS = {
  NONE: "",
  SIZE: "size",
  FORMAT: "format",
  DIMENSION: "dimension",
};

interface IUploadComponents {
  uploadedFile?: any;
  maxImageWidth: number;
  maxImageHeight: number;
  fileImageUrl: string;
  setFileImageUrl: React.Dispatch<React.SetStateAction<string>>;
  setUploadedFile: React.Dispatch<React.SetStateAction<{ name: string }>>;
  inputId: string;
  idForUploadButton: string;
  styleForInputLabelContainer?: any;
  styleForUploadedContainer?: any;
  styleForFileRequirementContainer?: any;
  haveMarginLeft?: boolean;
}

interface IFileRequirement {
  uploadedFileErrorType?: string;
}

interface IFileRequirementContainer {
  haveMarginLeft?: boolean;
}

const Container = styled.div`
  display: flex;
  height: 70%;
  margin-right: 80px;

  @media screen and (max-width: 980px) {
    margin-right: 0px;
    margin-top: 16px;
  }

  @media screen and (max-width: 560px) {
    flex-direction: column !important ;
  }
`;

const FileNameContainer = styled.div`
  display: flex;
`;

const FileName = styled.span`
  color: ${(props) => props.theme.colors.label};
`;

const IconsUploadContainer = styled.div`
  display: flex;
  margin-top: 20px;
`;

const FileRequirementContainer = styled.div<IFileRequirementContainer>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: ${(props) => (props.haveMarginLeft ? "22px" : "none")};

  @media screen and (max-width: 500px) {
    margin-left: 0;
    margin-top: 16px;
  }
`;

const FileRequirement = styled.div<IFileRequirement>`
  display: flex;
  color: ${(props) =>
    props.uploadedFileErrorType !== FILE_ERRORS.NONE ? colors.error : colors.black};
  white-space: pre-wrap;
  width: 100%;

  @media screen and (max-width: 980px) {
    font-size: 10px;
  }
`;

const UploadedFileRequirementContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 22px;
  width: 100%;
  margin-left: 16px;

  @media screen and (max-width: 1100px) {
    font-size: 14px;
  }
  @media screen and (max-width: 980px) {
    font-size: 10px;
  }

  @media screen and (max-width: 560px) {
    margin-left: 0;
  }
`;

const UploadedFileRequirement = styled.div<IFileRequirement>`
  display: flex;
  color: ${(props) =>
    props.uploadedFileErrorType !== FILE_ERRORS.NONE ? colors.error : colors.black};
  width: 100%;
`;

const InputLabelFileContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  vertical-align: top;

  @media screen and (max-width: 980px) {
    margin-top: 16px;
  }

  @media screen and (max-width: 500px) {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
`;

const LabelFile = styled.label`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  padding: 10px;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.body};
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 40px;
  width: 158px;
  height: 46px;

  @media screen and (max-width: 1200px) {
    font-size: 10px;
  }

  @media only screen and (max-width: 1050px) {
    padding: 8px;
    font-size: 12px;
    width: 126px;
    height: 36px;
  }

  @media screen and (max-width: 560px) {
    margin-right: 16px;
  }
`;

const InputFile = styled.input.attrs({ type: "file" })`
  display: none;
`;

const TitleAndIconsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const UploadImage: React.FC<IUploadComponents> = ({
  uploadedFile,
  setUploadedFile,
  maxImageWidth,
  maxImageHeight,
  fileImageUrl,
  setFileImageUrl,
  inputId,
  idForUploadButton,
  styleForInputLabelContainer,
  haveMarginLeft,
  styleForUploadedContainer,
  styleForFileRequirementContainer,
}) => {
  const imageDimension = t('dashboard.image.dimensions', {maxImageWidth: maxImageWidth, maxImageHeight: maxImageHeight});
  const imageFormat = t('dashboard.image.format');
  const messageImage = imageDimension + '\n' + imageFormat;

  const [uploadedFileErrorType, setUploadedFileErrorType] = useState<string>(
    FILE_ERRORS.NONE
  );
  const [errorMessage, setErrorMessage] = useState<string>(messageImage);
  const [showModal, setShowModal] = useState(false);

  const isSmallScreen = useMediaQuery({ query: "(max-width: 1050px)" });

  let uploadedImageDimensions = {};
  let uploadedFileError = FILE_ERRORS.NONE;
  let isFileUploadFailed = false;

  const checkFile = (file: File) => {
    const acceptedTypes = ["svg", "png", "jpg", "jpeg"];
    const fileType = file.type.replace("image/", "");
    if (!acceptedTypes.includes(fileType)) {
      uploadedFileError = FILE_ERRORS.FORMAT;
      setUploadedFileErrorType(FILE_ERRORS.FORMAT);
      isFileUploadFailed = true;
    }
  };

  const checkFileSize = (file: File) => {
    const FILE_SIZE_IN_BYTES = 1000000;
    if (file.size > FILE_SIZE_IN_BYTES) {
      uploadedFileError = FILE_ERRORS.SIZE;
      setUploadedFileErrorType(FILE_ERRORS.SIZE);
      isFileUploadFailed = true;
    }
  };

  const getImageDimensions = async (fileInput: any) => {
    const oFReader = new FileReader();
    oFReader.readAsDataURL(fileInput);
    oFReader.onload = async (event: any) => {
      let image = new Image();
      image.src = event.target.result;
      image.onload = function () {
        uploadedImageDimensions = { width: image.width, height: image.height };
      };
    };
  };

  const checkFileDimension = (fileDimension: any) => {
    if (
      fileDimension.width > maxImageWidth ||
      fileDimension.height > maxImageHeight
    ) {
      uploadedFileError = FILE_ERRORS.DIMENSION;
      setUploadedFileErrorType(FILE_ERRORS.DIMENSION);
      isFileUploadFailed = true;
    }
  };

  const checkImageErrorMessage = (uploadedFileError: any) => {
    switch (uploadedFileError) {
      case FILE_ERRORS.FORMAT:
        setErrorMessage(
          t('dashboard.image.error.format')!
        );
        return;
      case FILE_ERRORS.SIZE:
        setErrorMessage(
          t('dashboard.image.error.size')!
        );
        return;
      case FILE_ERRORS.DIMENSION:
        setErrorMessage(
          t('dashboard.image.error.dimensions', {maxImageWidth: maxImageWidth, maxImageHeight: maxImageHeight})!
        );
        return;
      case FILE_ERRORS.NONE:
        setErrorMessage(messageImage);
        return;
      default:
        return;
    }
  };

  const handleLoadFile = async (event: any) => {
    const [file]: File[] = event.target.files;

    checkFile(file);
    checkFileSize(file);
    getImageDimensions(file);
    await new Promise((resolve) => setTimeout(resolve, 900));
    checkFileDimension(uploadedImageDimensions);

    if (isFileUploadFailed) {
      checkImageErrorMessage(uploadedFileError);
      setFileImageUrl("");
      setUploadedFile({ name: "" });
    } else {
      uploadedFileError = FILE_ERRORS.NONE;
      setFileImageUrl(URL.createObjectURL(file));
      setUploadedFile(file);
      setUploadedFileErrorType(FILE_ERRORS.NONE);
      checkImageErrorMessage(uploadedFileError);
    }
  };

  const handleImagePreview = () => {
    setShowModal(true);
  };

  const handleRemoveFile = () => {
    uploadedFileError = FILE_ERRORS.NONE;
    setFileImageUrl("");
    setUploadedFile({ name: "" });
    setErrorMessage("");
    setUploadedFileErrorType(FILE_ERRORS.NONE);
    checkImageErrorMessage(uploadedFileError);
  };

  return (
    <>
      {uploadedFile.name === "" || uploadedFile.name === null ? (
        <InputLabelFileContent style={styleForInputLabelContainer}>
          <LabelFile className="label" htmlFor={inputId}>
            {isSmallScreen ? t('dashboard.image.add') : t('dashboard.image.addImage')}
          </LabelFile>
          <br />
          <InputFile
            type="file"
            id={inputId}
            onChange={(event) => handleLoadFile(event)}
          />
          <FileRequirementContainer
            haveMarginLeft={haveMarginLeft}
            style={styleForFileRequirementContainer}
          >
            <FileRequirement uploadedFileErrorType={uploadedFileErrorType}>
              {errorMessage}
            </FileRequirement>
          </FileRequirementContainer>
        </InputLabelFileContent>
      ) : (
        <Container style={styleForUploadedContainer}>
          <TitleAndIconsContainer>
            <FileNameContainer>
              <AttachmentIcon style={{ marginRight: "8px" }} />
              <FileName>{uploadedFile.name}</FileName>
            </FileNameContainer>
            <IconsUploadContainer>
              { fileImageUrl.trim() !== "" && 
                <ViewIcon
                  onClick={handleImagePreview}
                  style={{ marginRight: "24px" }}
                />
              }
              <UploadIcon
                handleLoadFile={handleLoadFile}
                idForUploadButton={idForUploadButton}
              />
              <DeleteIcon
                style={{ marginLeft: "24px" }}
                handleRemoveFile={handleRemoveFile}
              />
            </IconsUploadContainer>
          </TitleAndIconsContainer>
          <UploadedFileRequirementContainer>
            <UploadedFileRequirement
              uploadedFileErrorType={uploadedFileErrorType}
            >{imageDimension}</UploadedFileRequirement>
            <UploadedFileRequirement
              uploadedFileErrorType={uploadedFileErrorType}
            >
              {imageFormat}
            </UploadedFileRequirement>
          </UploadedFileRequirementContainer>
          {showModal && (
            <PreviewImageModal
              setShowModal={setShowModal}
              imageUrlForModal={fileImageUrl}
              idForImage="description-block-modal"
            />
          )}
        </Container>
      )}
    </>
  );
};

export default UploadImage;
